import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ShoppingCart, ArrowLeft, Star, Minus, Plus,
    ShieldCheck, Truck, RotateCcw, Package, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { baseURL } from '../../utils/api';
import { useCachedFetch } from '../../utils/cache';
import './ProductDetail.css';

const resolveImage = (img) => {
    if (!img) return null;
    if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) return img;
    return `${baseURL}${img}`;
};

const ProductDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('details');
    const [adding, setAdding] = useState(false);
    const { addToCart } = useCart();

    const { data: product, loading, error } = useCachedFetch(
        slug ? `/api/products/${slug}` : null,
        { ttl: 10 * 60 * 1000, enabled: !!slug }
    );
    const notFound = !!error || (!loading && !product);

    if (loading) {
        return (
            <div className="ec-pd-loading">
                <div className="ec-spinner" />
                <p>Loading product details...</p>
            </div>
        );
    }

    if (notFound || !product) {
        return (
            <div className="ec-pd-error">
                <div className="ec-pd-error-icon"><Package size={40} /></div>
                <h2>Product not found</h2>
                <p>The product you're looking for may have been removed or is no longer available.</p>
                <div className="ec-pd-error-actions">
                    <Link to="/shop" className="ec-btn ec-btn--primary">
                        <ArrowLeft size={16} /> Back to Shop
                    </Link>
                </div>
            </div>
        );
    }

    const imageSrc = resolveImage(product.image);
    const inStock = product.stock > 0;
    const lowStock = inStock && product.stock <= 5;

    const handleAddToCart = () => {
        if (!inStock) return;
        setAdding(true);
        addToCart(product, quantity);
        setTimeout(() => {
            setAdding(false);
            navigate('/cart');
        }, 300);
    };

    return (
        <div className="ec-pd-root">
            <div className="ec-pd-container">
                <Link to="/shop" className="ec-back-link">
                    <ArrowLeft size={16} /> Back to Collection
                </Link>

                <div className="ec-pd-grid">
                    {/* Image Gallery */}
                    <div className="ec-pd-gallery">
                        <div className="ec-pd-main-img">
                            {imageSrc ? (
                                <img src={imageSrc} alt={product.name} />
                            ) : (
                                <div className="ec-pd-no-img">
                                    <Package size={60} />
                                    <span>No image available</span>
                                </div>
                            )}
                        </div>
                        {product.category && (
                            <div className="ec-pd-img-badge">{product.category}</div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="ec-pd-info">
                        <div className="ec-pd-meta">
                            <span className="ec-pd-cat">{product.category || 'Product'}</span>
                            <div className="ec-pd-rating">
                                <Star size={14} fill="#f59e0b" color="#f59e0b" />
                                <span>4.8 <em>(124 reviews)</em></span>
                            </div>
                        </div>

                        <h1 className="ec-pd-title">{product.name}</h1>

                        <div className="ec-pd-price-row">
                            <span className="ec-pd-price">Rs. {product.price.toLocaleString()}</span>
                            {inStock ? (
                                <span className={`ec-pd-stock ${lowStock ? 'low' : 'ok'}`}>
                                    {lowStock ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
                                    {lowStock ? `Only ${product.stock} left` : 'In Stock'}
                                </span>
                            ) : (
                                <span className="ec-pd-stock out">
                                    <AlertTriangle size={14} /> Out of Stock
                                </span>
                            )}
                        </div>

                        <div className="ec-pd-desc">
                            <p>
                                {product.description ||
                                    "No description available for this product yet. It's built with premium quality and designed for everyday use."}
                            </p>
                        </div>

                        <div className="ec-pd-actions">
                            <div className="ec-quantity-selector">
                                <button
                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                    disabled={quantity <= 1}
                                    aria-label="Decrease quantity"
                                >
                                    <Minus size={16} />
                                </button>
                                <span>{quantity}</span>
                                <button
                                    onClick={() => setQuantity(q => Math.min(product.stock || 99, q + 1))}
                                    disabled={inStock && quantity >= product.stock}
                                    aria-label="Increase quantity"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <button
                                className="ec-add-btn"
                                onClick={handleAddToCart}
                                disabled={!inStock || adding}
                            >
                                <ShoppingCart size={18} />
                                {adding ? 'Adding...' : (inStock ? 'Add to Cart' : 'Out of Stock')}
                            </button>
                        </div>

                        <div className="ec-pd-trust-badges">
                            <div className="ec-trust-item">
                                <div className="ec-trust-icon"><Truck size={16} /></div>
                                <div>
                                    <strong>Fast Delivery</strong>
                                    <span>2–5 days across Pakistan</span>
                                </div>
                            </div>
                            <div className="ec-trust-item">
                                <div className="ec-trust-icon"><ShieldCheck size={16} /></div>
                                <div>
                                    <strong>Quality Guaranteed</strong>
                                    <span>100% authentic products</span>
                                </div>
                            </div>
                            <div className="ec-trust-item">
                                <div className="ec-trust-icon"><RotateCcw size={16} /></div>
                                <div>
                                    <strong>Easy Returns</strong>
                                    <span>7-day return policy</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <section className="ec-pd-extra">
                    <div className="ec-pd-tabs">
                        <button
                            className={`ec-tab ${activeTab === 'details' ? 'active' : ''}`}
                            onClick={() => setActiveTab('details')}
                        >
                            Details
                        </button>
                        <button
                            className={`ec-tab ${activeTab === 'shipping' ? 'active' : ''}`}
                            onClick={() => setActiveTab('shipping')}
                        >
                            Shipping & Returns
                        </button>
                        <button
                            className={`ec-tab ${activeTab === 'reviews' ? 'active' : ''}`}
                            onClick={() => setActiveTab('reviews')}
                        >
                            Reviews
                        </button>
                    </div>

                    <div className="ec-pd-tab-content">
                        {activeTab === 'details' && (
                            <>
                                <p>
                                    {product.description || 'This product represents the pinnacle of modern design and utility. Every detail has been refined to ensure the best possible user experience.'}
                                </p>
                                <ul>
                                    <li>Premium build quality with durable materials</li>
                                    <li>Designed for ergonomic comfort and ease of use</li>
                                    <li>Compatible with standard accessories</li>
                                    <li>Sleek aesthetic that complements any setting</li>
                                </ul>
                            </>
                        )}
                        {activeTab === 'shipping' && (
                            <>
                                <p>
                                    We ship nationwide across Pakistan within <strong>2–5 business days</strong>.
                                    Standard shipping fee is Rs. 250 per order. Free shipping on select promotions.
                                </p>
                                <ul>
                                    <li>Delivered by trusted courier partners</li>
                                    <li>Order tracking available once shipped</li>
                                    <li>7-day return window on unopened items</li>
                                    <li>Cash on Delivery available for most cities</li>
                                </ul>
                            </>
                        )}
                        {activeTab === 'reviews' && (
                            <div className="ec-pd-reviews-empty">
                                <Star size={32} fill="#f59e0b" color="#f59e0b" />
                                <h4>No reviews yet</h4>
                                <p>Be the first to review this product after your purchase.</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ProductDetail;
